import { terminal } from '@/constant/index';

export type IconFitMode = 'square' | 'original';

/** 推荐比例（线上）：方框基准 */
const SIZE_MAP = {
  Small: 30,
  Medium: 50,
  Big: 80
} as const;

/**
 * 端尺寸表：推荐比例竖图动态 / original 定高共用
 * 小 PC40/移28；中 PC50/移35；大 PC65/移46
 */
const DEVICE_SIZE_MAP = {
  Small: { mobile: 28, desktop: 40 },
  Medium: { mobile: 35, desktop: 50 },
  Big: { mobile: 46, desktop: 65 }
} as const;

/**
 * 推荐比例竖图阈值：宽/高 < 该值进动态档（与改版前 C 端一致，保持 0.7）
 */
export const LEGACY_VERTICAL_RATIO_THRESHOLD = 0.7;

/** 关闭按钮尺寸枚举：10～30，步长 2 */
export const ICON_CLOSE_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30] as const;

export const DEFAULT_ICON_CLOSE_COLOR = 'rgba(255, 255, 255, 1)';
/** 关闭按钮背景默认：#000 70% 透明 */
export const DEFAULT_ICON_CLOSE_BG_COLOR = 'rgba(0, 0, 0, 0.7)';
/** 灰度缺省 / 新建默认关闭尺寸 */
export const DEFAULT_ICON_CLOSE_SIZE = 16;
/** 早期 B 端 ensure/默认下拉误写入的关闭尺寸 */
export const LEGACY_DEFAULT_ICON_CLOSE_SIZE = 18;
/** 早期默认关闭背景（污染识别兼容） */
const LEGACY_DEFAULT_ICON_CLOSE_BG_COLORS = [
  'rgba(0,0,0,0.7)',
  'rgba(51,51,51,1)'
];

function normalizeCssColor(color?: string): string {
  return (color || '').replace(/\s+/g, '').toLowerCase();
}

function isDefaultIconCloseBgColor(color?: string): boolean {
  if (!color) {
    return true;
  }
  const normalized = normalizeCssColor(color);
  return LEGACY_DEFAULT_ICON_CLOSE_BG_COLORS.some((c) => normalized === c);
}

function getSizeKey(iconSizeType?: string): keyof typeof SIZE_MAP {
  if (iconSizeType === 'Small' || iconSizeType === 'Big') {
    return iconSizeType;
  }
  return 'Medium';
}

function getDeviceSize(iconSizeType?: string): number {
  const map = DEVICE_SIZE_MAP[getSizeKey(iconSizeType)];
  return !terminal ? map.mobile : map.desktop;
}

export function isOriginalFitMode(mode?: string): boolean {
  return mode === 'original';
}

export function getIconDockSide(iconPosition?: string): 'Left' | 'Right' {
  return (iconPosition || '').startsWith('Right') ? 'Right' : 'Left';
}

/**
 * 计算 icon 容器宽高
 * - square / 缺字段：用户档方框（小30/中50/大80）；ratio < LEGACY_VERTICAL_RATIO_THRESHOLD 走竖图动态档
 * - original：定高，宽按原图比例（不改）
 */
export function calcIconBoxSize(options: {
  iconSizeType?: string;
  iconFitMode?: IconFitMode | string;
  naturalWidth?: number;
  naturalHeight?: number;
}): { width: number; height: number; useLegacyVertical: boolean } {
  const fitMode = isOriginalFitMode(options.iconFitMode) ? 'original' : 'square';
  const naturalW = options.naturalWidth || 0;
  const naturalH = options.naturalHeight || 0;
  const ratio = naturalW > 0 && naturalH > 0 ? naturalW / naturalH : 1;

  if (fitMode !== 'original') {
    // 竖图判定保持线上 0.7
    const useLegacyVertical = ratio < LEGACY_VERTICAL_RATIO_THRESHOLD && naturalW > 0;
    const base = useLegacyVertical ? getDeviceSize(options.iconSizeType) : SIZE_MAP[getSizeKey(options.iconSizeType)];
    return { width: base, height: base, useLegacyVertical };
  }

  const height = getDeviceSize(options.iconSizeType);
  const safeRatio = naturalW > 0 && naturalH > 0 ? ratio : 1;
  return { width: Math.max(1, Math.round(height * safeRatio)), height, useLegacyVertical: false };
}

export function isConfiguredIconCloseSize(size?: number): boolean {
  return !!(size && size >= 10 && size <= 30);
}

/**
 * 是否存在明确的关闭高级配置（样式/位置/颜色）。
 * 仅有 iconCloseSize（早期误写默认 18）不算。
 */
export function hasExplicitIconCloseConfig(position?: {
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  if (!position) {
    return false;
  }
  return !!(
    position.iconCloseStyle ||
    position.iconClosePosition ||
    position.iconCloseColor ||
    position.iconCloseBgColor
  );
}

/**
 * 早期误写污染：size=18，且颜色仍是默认（或未写）。
 * 图片外 / 图片内叠加均不信任，回退默认 16（与 B 对齐）。
 */
export function isLegacyPollutedIconCloseSize(position?: {
  iconCloseSize?: number;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  if (!position || position.iconCloseSize !== LEGACY_DEFAULT_ICON_CLOSE_SIZE) {
    return false;
  }
  if (
    position.iconCloseColor &&
    normalizeCssColor(position.iconCloseColor) !== normalizeCssColor(DEFAULT_ICON_CLOSE_COLOR)
  ) {
    return false;
  }
  if (
    position.iconCloseBgColor &&
    !isDefaultIconCloseBgColor(position.iconCloseBgColor)
  ) {
    return false;
  }
  return true;
}

export function getTrustedIconCloseSize(position?: {
  iconCloseSize?: number;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): number | undefined {
  if (!position || isLegacyPollutedIconCloseSize(position) || !isConfiguredIconCloseSize(position.iconCloseSize)) {
    return undefined;
  }
  return position.iconCloseSize;
}

/** 贴近最近枚举档（等距取较大），供下拉回显 */
export function snapIconCloseSizeToEnum(
  size: number,
  options: readonly number[] = ICON_CLOSE_SIZE_OPTIONS
): number {
  let best = options[0];
  for (let i = 0; i < options.length; i++) {
    const o = options[i];
    const d = Math.abs(o - size);
    const bd = Math.abs(best - size);
    if (d < bd || (d === bd && o > best)) {
      best = o;
    }
  }
  return best;
}

/**
 * 关闭按钮尺寸
 * - 优先用户配置（10～30，排除早期误写 18）
 * - 增强模式缺省 / 新建：默认 16
 * - 线上 legacy：盒宽 × 30%
 */
export function calcIconCloseSize(
  boxWidth: number,
  _boxHeight?: number,
  configuredSize?: number,
  legacyPercent = false,
  position?: {
    iconCloseSize?: number;
    iconCloseStyle?: string;
    iconClosePosition?: string;
    iconCloseColor?: string;
    iconCloseBgColor?: string;
  }
): number {
  const trusted = position
    ? getTrustedIconCloseSize(position)
    : isConfiguredIconCloseSize(configuredSize)
      ? configuredSize
      : undefined;
  if (trusted != null) {
    return trusted as number;
  }
  if (legacyPercent) {
    return Math.max(1, Math.round((boxWidth || 50) * 0.3));
  }
  return DEFAULT_ICON_CLOSE_SIZE;
}

/** 关闭默认角：左 icon → 右上；右 icon → 左上 */
export function resolveIconClosePosition(iconPosition?: string, configured?: string): string {
  if (configured) {
    return configured;
  }
  return getIconDockSide(iconPosition) === 'Right' ? 'LeftTop' : 'RightTop';
}

/** inside 且同侧贴边：让出半个关闭钮；+1 避免抗锯齿/亚像素裁切（至少贴齐，宁可多露 1px） */
export function calcIconCloseDockInset(
  iconSide: 'Left' | 'Right',
  closePosition?: string,
  closeStyle?: string,
  closeSize = DEFAULT_ICON_CLOSE_SIZE
): number {
  if (closeStyle !== 'inside') {
    return 0;
  }
  const closeOnLeft = (closePosition || '').startsWith('Left');
  const closeOnRight = (closePosition || '').startsWith('Right');
  if ((iconSide === 'Left' && closeOnLeft) || (iconSide === 'Right' && closeOnRight)) {
    return Math.round(closeSize / 2) + 1;
  }
  return 0;
}

/**
 * 是否启用关闭按钮新布局：以 iconClose 为准。
 * 开启后优先用已有 style/位置/颜色/尺寸，缺省再走兜底（尺寸 16、底色黑 0.7 等）。
 */
export function hasIconCloseEnhance(position?: {
  iconClose?: boolean;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseSize?: number;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  return !!position?.iconClose;
}

type BoxSize = { width: number; height: number; useLegacyVertical: boolean };

/**
 * 推荐模式图片高度约束（对齐改版前 C 端 iconModt，不影响 original）
 * - 竖图（ratio<0.7）：maxHeight inherit（按宽等比放开高度）
 * - 方图：maxHeight = 盒子边长（锁在正方形内 contain）
 */
export function calcRecommendContentHeightCss(
  useLegacyVertical: boolean,
  boxSide?: number
): {
  maxHeight: string;
} {
  if (useLegacyVertical) {
    return { maxHeight: 'inherit' };
  }
  return { maxHeight: `${boxSide || 50}px` };
}

/** @deprecated 使用 calcRecommendContentHeightCss；保留导出避免外部引用断裂 */
export function calcSquareContentMaxHeight(options: {
  box: BoxSize;
  iconSizeType?: string;
  naturalWidth?: number;
  naturalHeight?: number;
}): string {
  return calcRecommendContentHeightCss(options.box.useLegacyVertical, options.box.width).maxHeight;
}

/** 浮层外层宽高（square 固定方框宽；竖图/关闭增强需可见溢出） */
export function buildIconOpenSizeCss(box: BoxSize, fitMode?: string): Record<string, string> {
  const isOriginal = isOriginalFitMode(fitMode);
  const css: Record<string, string> = {
    width: `${box.width}px`,
    height: `${(isOriginal ? box.height : box.width)}px`
  };
  if (isOriginal) {
    css.minHeight = `${box.height}px`;
    css.maxWidth = 'none';
  }
  // 竖图超出正方形、外侧关闭超出盒子：始终可见溢出
  css.overflow = 'visible';
  return css;
}

/** 内层容器 + img 样式（与线上 square / original 规则对齐） */
export function buildIconContentStyles(
  box: BoxSize,
  fitMode: string | undefined,
  opts: {
    closeEnhance?: boolean;
    iconPosition?: string;
    iconSizeType?: string;
    naturalWidth?: number;
    naturalHeight?: number;
  }
): { wrap: Record<string, string | number | undefined>; img: Record<string, string | number | undefined> } {
  const isOriginal = isOriginalFitMode(fitMode);
  const isRight = ['RightTop', 'RightMiddle', 'RightBottom'].includes(opts.iconPosition || '');

  if (isOriginal) {
    return {
      wrap: {
        maxWidth: 'none',
        maxHeight: 'none',
        width: `${box.width}px`,
        height: `${box.height}px`,
        minHeight: `${box.height}px`,
        overflow: 'visible',
        position: 'relative',
        top: 'auto',
        left: 'auto',
        transform: 'none',
        textAlign: isRight ? 'left' : 'right',
        fontSize: 0
      },
      img: {
        height: `${box.height}px`,
        minHeight: `${box.height}px`,
        width: 'auto',
        maxWidth: 'none',
        maxHeight: 'none',
        flexShrink: 0,
        display: 'block',
        objectFit: 'contain'
      }
    };
  }

  // 推荐模式：对齐改版前 C —— 方图 max=盒边；竖图 maxHeight=inherit
  // 内层相对外层方盒居中：bottom/top 安全距离作用在视觉中心，避免竖图向下溢出贴底
  const heightCss = calcRecommendContentHeightCss(box.useLegacyVertical, box.width);
  return {
    wrap: {
      maxWidth: `${box.width}px`,
      maxHeight: heightCss.maxHeight,
      overflow: 'visible',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: isRight ? 'left' : 'right',
      fontSize: 0
    },
    img: {
      maxWidth: `${box.width}px`,
      maxHeight: heightCss.maxHeight,
      flexShrink: 0,
      display: 'block',
      objectFit: 'contain'
    }
  };
}
